import { StarIcon } from "@heroicons/react/20/solid";
import { ago, NoteEntityId, NoteId, transferredFromLabel } from "@sp-crm/core";
import { AuthorName } from "components/byline/author-name";
import { EditNote, IEntityNote } from "components/notes/edit";
import { NoteAuthor } from "components/notes/note-author";
import { EditButton } from "components/ui/action-button";
import { ClampedText } from "components/ui/clamped-text";
import {
    GetNotesForEntityQuery,
    UpdateNoteMutationVariables,
    useGetNotesForEntityQuery,
    useUpdateNoteMutation,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { breakNewlines } from "util/text";

interface ImportantNoteAttributionProps {
    note: GetNotesForEntityQuery["getNotesForEntity"][number];
}

const ImportantNoteAttribution: React.FC<ImportantNoteAttributionProps> = props => {
    const { note } = props;
    return (
        <div className="text-xs text-brand-700 space-y-0.5">
            <p>
                <NoteAuthor note={note} authorPrefix="Added by " /> {ago(note.dateAdded)}
            </p>
            {note.wasEdited ? (
                <p>
                    Last edited by <AuthorName authorId={note.editorId} />{" "}
                    {ago(note.updatedAt)}
                </p>
            ) : null}
        </div>
    );
};

const ImportantNoteFrame: React.FC<{
    children: React.ReactNode;
    action: React.ReactNode;
}> = ({ children, action }) => (
    <div className="rounded-md bg-brand-100 ring-1 ring-inset ring-brand-200 p-3">
        <div className="flex items-start gap-2">
            <StarIcon
                aria-hidden="true"
                className="mt-0.5 size-5 shrink-0 text-brand-500"
            />
            <div className="min-w-0 flex-1">{children}</div>
            {action}
        </div>
    </div>
);

const ImportantNoteText: React.FC<{ text: string }> = ({ text }) => (
    <ClampedText lines={2}>
        <h3 className="twoverride inline text-sm font-medium text-brand-800">
            Important Note:
        </h3>{" "}
        {breakNewlines(text)}
    </ClampedText>
);

export interface ReadOnlyImportantNoteSource {
    text: string;
    transferBusinessName?: string | null;
}

export const ReadOnlyImportantNote: React.FC<{ note: ReadOnlyImportantNoteSource }> = ({
    note,
}) => (
    <ImportantNoteFrame action={null}>
        <div className="text-sm text-brand-900">
            <ImportantNoteText text={note.text} />
            {note.transferBusinessName ? (
                <div className="mt-2 text-xs text-brand-700">
                    <p>{transferredFromLabel(note.transferBusinessName)}</p>
                </div>
            ) : null}
        </div>
    </ImportantNoteFrame>
);

interface ImportantNoteBannerProps {
    entityId: NoteEntityId;
    className: string;
}

export const ImportantNoteBanner: React.FC<ImportantNoteBannerProps> = props => {
    const { entityId, className } = props;
    const query = useGetNotesForEntityQuery({ entityId });
    const queryClient = useQueryClient();
    const updateMutation = useUpdateNoteMutation();
    const [isEditing, setIsEditing] = React.useState(false);

    const pinnedNote = query.data?.getNotesForEntity.find(n => n.isPinned && !n.isDraft);

    const onEditNote = React.useCallback(
        async (noteId: NoteId, text: string, dateAdded: Date, isPinned: boolean) => {
            const payload: UpdateNoteMutationVariables = {
                entityId,
                noteId,
                params: {
                    text,
                    isPublished: true,
                    dateAddedOverride: dateAdded,
                    isPinned,
                },
            };
            const result = await updateMutation.mutateAsync(payload);
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n =>
                            n._id === result.updateNote._id ? result.updateNote : n,
                        );
                    });
                },
            );
            setIsEditing(false);
        },
        [entityId, updateMutation, queryClient],
    );

    if (!pinnedNote) {
        return null;
    }

    const editableNote: IEntityNote = {
        _id: pinnedNote._id,
        authorId: pinnedNote.authorId,
        text: pinnedNote.text,
        dateAdded: new Date(pinnedNote.dateAdded),
        dateAddedManualOverride: new Date(pinnedNote.dateAdded),
        wasEdited: pinnedNote.wasEdited,
        isPinned: pinnedNote.isPinned,
        editorId: pinnedNote.editorId,
        updatedAt: new Date(pinnedNote.updatedAt),
    };

    return (
        <div className={className}>
            <ImportantNoteFrame
                action={
                    isEditing ? null : (
                        <EditButton
                            size="sm"
                            backgroundColor="bg-gray-100"
                            onClick={() => setIsEditing(true)}
                        />
                    )
                }>
                {isEditing ? (
                    <>
                        <h3 className="twoverride text-sm font-medium text-brand-800">
                            Important Note
                        </h3>
                        <EditNote
                            note={editableNote}
                            pinnedNoteId={pinnedNote._id}
                            onEditNote={onEditNote}
                            leaveEditMode={() => setIsEditing(false)}
                        />
                        <div className="mt-2">
                            <ImportantNoteAttribution note={pinnedNote} />
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-brand-900">
                        <ImportantNoteText text={pinnedNote.text} />
                        {pinnedNote.transferBusinessName ? (
                            <div className="mt-2">
                                <ImportantNoteAttribution note={pinnedNote} />
                            </div>
                        ) : null}
                    </div>
                )}
            </ImportantNoteFrame>
        </div>
    );
};
